import _ from 'underscore';
import { createSelector } from 'reselect';

import { generateRawSelectors } from 'helpers/RessourceSelectorsFactoryHelpers';

import COOKIES_BY_GROUP from '../constants/Cookies';
import FIRST_PARTY_COOKIE from '../constants/FirstPartyCookie';

export const INIT_CMP_APP = 'INIT_CMP_APP';
export const INIT_CMP_SUCCESS = 'INIT_CMP_SUCCESS';

export const SET_ACCEPTED_COOKIES = 'SET_ACCEPTED_COOKIES';

export const HIDE_BANNER = 'HIDE_BANNER';

export const OPEN_CMP_MODAL = 'OPEN_CMP_MODAL';
export const CLOSE_CMP_MODAL = 'CLOSE_CMP_MODAL';

export const ACCEPT_ALL_FROM_BANNER = 'ACCEPT_ALL_FROM_BANNER';
export const REJECT_ALL_FROM_BANNER = 'REJECT_ALL_FROM_BANNER';
export const ACCEPT_ALL_COOKIES = 'ACCEPT_ALL_COOKIES';
export const REFUSE_ALL_COOKIES = 'REFUSE_ALL_COOKIES';

export const SET_COOKIE_GROUP = 'SET_COOKIE_GROUP';
export const SET_COOKIE = 'SET_COOKIE';

export const ADD_TO_COOKIES_LIST = 'ADD_TO_COOKIES_LIST';
export const REMOVE_FROM_COOKIES_LIST = 'REMOVE_FROM_COOKIES_LIST';

export const SET_COOKIE_PREFERENCES = 'SET_COOKIE_PREFERENCES';
export const SET_COOKIE_TIMER = 'SET_COOKIE_TIMER';

export const RELOAD_PAGE = 'RELOAD_PAGE';

export const SET_COLLAPSED_STATE = 'SET_COLLAPSED_STATE';
export const ADD_TO_UNCOLLAPSED_GROUPS = 'ADD_TO_UNCOLLAPSED_GROUPS';
export const REMOVE_FROM_UNCOLLAPSED_GROUPS = 'REMOVE_FROM_UNCOLLAPSED_GROUPS';

export const SET_WIDGET_COOKIES = 'SET_WIDGET_COOKIES';

export const reducerName = 'AppCMP';

export const APP_STATUS = {
    INITIALIZING: 0,
    INITIALIZED: 1,
};

export const initialState = {
    status: APP_STATUS.INITIALIZING,
    isBannerHidden: false,
    acceptedCookies: [],
    cookiesGroupsConfiguration: COOKIES_BY_GROUP,
    firstPartyCookieConfiguration: FIRST_PARTY_COOKIE,
    uncollapsedGroups: [],
};

export default function AppCMP(
    state = initialState,
    action,
) {
    switch (action.type) {
        case INIT_CMP_SUCCESS:
            return {
                ...state,
                status: APP_STATUS.INITIALIZED,
            };
        case SET_ACCEPTED_COOKIES:
            return {
                ...state,
                acceptedCookies: action.payload.acceptedCookiesFromDocument,
            };
        case HIDE_BANNER:
            return {
                ...state,
                isBannerHidden: true,
            };
        case ADD_TO_COOKIES_LIST:
            return {
                ...state,
                acceptedCookies: _.uniq([
                    ...state.acceptedCookies,
                    action.payload.cookie,
                ], false, 'id'),
            };
        case REMOVE_FROM_COOKIES_LIST:
            return {
                ...state,
                acceptedCookies: _.reject(
                    state.acceptedCookies,
                    action.payload.cookie,
                ),
            };
        case ADD_TO_UNCOLLAPSED_GROUPS:
            return {
                ...state,
                uncollapsedGroups: _.uniq([
                    ...state.uncollapsedGroups,
                    action.payload.cookieGroupId,
                ], false),
            };
        case REMOVE_FROM_UNCOLLAPSED_GROUPS:
            return {
                ...state,
                uncollapsedGroups: _.reject([
                    state.uncollapsedGroups,
                    action.payload.cookieGroupId,
                ]),
            };
        default:
            break;
    }
    return state;
}

/*
    ActionCreators
 */

export const initCMPApp = () => ({ type: INIT_CMP_APP });

export const initCMPSuccess = () => ({ type: INIT_CMP_SUCCESS });

export const openModal = () => ({ type: OPEN_CMP_MODAL });

export const closeModal = () => ({ type: CLOSE_CMP_MODAL });

export const acceptAllFromBanner = () => ({ type: ACCEPT_ALL_FROM_BANNER });

export const rejectAllFromBanner = () => ({ type: REJECT_ALL_FROM_BANNER });

export const hideBanner = () => ({ type: HIDE_BANNER });

export const acceptAllCookies = () => ({ type: ACCEPT_ALL_COOKIES });

export const refuseAllCookies = () => ({ type: REFUSE_ALL_COOKIES });

export const setCookieTimer = () => ({ type: SET_COOKIE_TIMER });

export const reloadPage = () => ({ type: RELOAD_PAGE });

export const setWidgetCookies = () => ({ type: SET_WIDGET_COOKIES });

export function setAcceptedCookies(acceptedCookiesFromDocument) {
    return {
        type: SET_ACCEPTED_COOKIES,
        payload: {
            acceptedCookiesFromDocument,
        },
    };
}

export function setCookie(cookieId, isAccepted) {
    return {
        type: SET_COOKIE,
        payload: {
            cookieId,
            isAccepted,
        },
    };
}

export function setCookieGroup(cookiesIds, areAccepted) {
    return {
        type: SET_COOKIE_GROUP,
        payload: {
            cookiesIds,
            areAccepted,
        },
    };
}

export function addToCookiesList(cookie) {
    return {
        type: ADD_TO_COOKIES_LIST,
        payload: {
            cookie,
        },
    };
}

export function removeFromCookiesList(cookie) {
    return {
        type: REMOVE_FROM_COOKIES_LIST,
        payload: {
            cookie,
        },
    };
}

export const setCookiePreferences = () => ({ type: SET_COOKIE_PREFERENCES });

export function setCollapsedState(cookieGroupId, isCollapsed) {
    return {
        type: SET_COLLAPSED_STATE,
        payload: {
            cookieGroupId,
            isCollapsed,
        },
    };
}

export function addToUncollapsedGroups(cookieGroupId) {
    return {
        type: ADD_TO_UNCOLLAPSED_GROUPS,
        payload: {
            cookieGroupId,
        },
    };
}

export function removeFromUncollapsedGroups(cookieGroupId) {
    return {
        type: REMOVE_FROM_UNCOLLAPSED_GROUPS,
        payload: {
            cookieGroupId,
        },
    };
}

/*
    Selectors
 */

const rawSelectors = generateRawSelectors(initialState, reducerName);

export const {
    getAcceptedCookiesSelector,
    getCookiesGroupsConfigurationSelector,
    getFirstPartyCookieConfigurationSelector,
    getIsBannerHiddenSelector,
    getStatusSelector: getRawStatusSelector,
} = rawSelectors;

export const isInitiatedSelector = createSelector(
    [getRawStatusSelector],
    (rawStatus) => rawStatus === APP_STATUS.INITIALIZED,
);

export function getCookiesGroupSelector(state, cookieGroupId) {
    const cookiesGroupsConfiguration = getCookiesGroupsConfigurationSelector(state);
    return _.find(cookiesGroupsConfiguration, { id: cookieGroupId });
}

export function getCookiesByGroupSelector(state, cookieGroupId) {
    const cookieGroup = getCookiesGroupSelector(state, cookieGroupId);
    if (!cookieGroup) return [];
    return cookieGroup.cookies;
}

export function getCookiesIdsByGroupSelector(state, cookieGroupId) {
    const cookiesByGroup = getCookiesByGroupSelector(state, cookieGroupId);
    if (!cookiesByGroup) return [];
    return _.pluck(cookiesByGroup, 'id');
}

export const getAllCookiesSelector = createSelector(
    [getCookiesGroupsConfigurationSelector],
    (cookiesGroupsConfiguration) => {
        const cookiesCollections = _.pluck(cookiesGroupsConfiguration, 'cookies');
        // Concat the cookies collections from each group
        return Array.prototype.concat.apply([], cookiesCollections);
    },
);

export const getAllCookiesIdsSelector = createSelector(
    [getFirstPartyCookieConfigurationSelector, getAllCookiesSelector],
    (firstPartyCookie, cookies) => {
        const thirdPartyCookiesIds = _.pluck(cookies, 'id');
        return [firstPartyCookie.id].concat(thirdPartyCookiesIds);
    },
);

export function getCookieSelector(state, cookieId) {
    const allCookies = getAllCookiesSelector(state);
    return _.find(allCookies, { id: cookieId });
}

export function isCookieAcceptedSelector(state, cookieId) {
    const acceptedCookies = getAcceptedCookiesSelector(state);
    return _.some(acceptedCookies, { id: cookieId });
}

export function isCookieGroupAcceptedSelector(state, cookieGroupId) {
    const acceptedCookies = getAcceptedCookiesSelector(state);
    const cookiesIds = getCookiesIdsByGroupSelector(state, cookieGroupId);
    return _.every(cookiesIds, (cookieId) => _.some(
        acceptedCookies, { id: cookieId },
    ));
}

export function isCookieGroupCollapsedSelector(state, cookieGroupId) {
    return state[reducerName].uncollapsedGroups.indexOf(cookieGroupId) < 0;
}
